import { z } from 'zod';

import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import {
  ZodInstrumentBrokerStatus,
  ZodInstrumentDocumentLanguage,
  ZodInstrumentMutualFundLegalStatus,
  zodFundinfoPricingFrequency,
} from '@Common/types/bo/instruments';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { zodDecimalAmount } from '@Common/types/currency/currency';
import {
  ZodB2bInstrumentAssetType,
  ZodB2bInstrumentType,
  ZodInstrumentAssetType,
  ZodInstrumentManagementType,
  ZodInstrumentMutualFundAssetClass,
  ZodInstrumentType,
} from '@Common/types/instruments/instrument';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodDateString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodIntegerLike } from '@Common/types/zod/zod-number-like';
import { ZodNumberQuery } from '@Common/types/zod/zod-number-query';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodCountry = ZodTrimmedString.length(2);

export const ZodGetInstrumentsResponseItem = z.object({
  id: ZodId,
  short_name: ZodTrimmedString.nullable(),
  isin: ZodTrimmedString.nullable(),
  pricing_frequency: zodFundinfoPricingFrequency.nullable(),
  management_company: ZodTrimmedString.nullable(),
  risk: z.number().nullable(),
  performance: z.number().nullable(),
});
export type GetInstrumentsResponseItem = z.TypeOf<typeof ZodGetInstrumentsResponseItem>;
const ZodGetInstrumentsResponse = z.array(ZodGetInstrumentsResponseItem);
export type GetInstrumentsResponse = z.TypeOf<typeof ZodGetInstrumentsResponse>;

export const ZodGetInstrumentOneResponse = z.object({
  ...ZodGetInstrumentsResponseItem.omit({
    performance: true,
  }).shape,
  symbol: ZodTrimmedString,
  name: ZodTrimmedString.nullable(),
  exchange: ZodTrimmedString.nullable(),
  country_code: ZodCountry.nullable(),
  class: ZodTrimmedString.nullable(),
  status: ZodInstrumentBrokerStatus,
  tradable: z.boolean(),
  fractionable: z.boolean().nullable(),
  created_at: z.date(),
  logo_path: ZodTrimmedString.nullable(),
  short_description: ZodTrimmedString.nullable(),
  is_enabled: z.boolean(),
  is_halted: z.boolean(),
  type: ZodInstrumentType.or(ZodB2bInstrumentType),
  asset_type: ZodInstrumentAssetType.or(ZodB2bInstrumentAssetType),
  five_year_beta: z.number().nullable(),
  five_year_beta_fallback: z.number().nullable(),
  is_pea_eligible: z.boolean(),
  settlement_location: ZodTrimmedString.nullable(),
  sub_custodian_account_number: ZodTrimmedString.nullable(),
  has_pending_orders: z.boolean(),
  has_open_positions: z.boolean(),
  is_price_fetched: z.boolean(),
  data_fetch_completed: z.boolean(),
  risk: z.number().nullable(),
  mutual_fund_asset_class: ZodInstrumentMutualFundAssetClass.nullable(),
  management_company: ZodTrimmedString.nullable(),
  about: ZodTrimmedString.nullable(),
  management_fee_pct: z.number().nullable(),
  management_type: ZodInstrumentManagementType.nullable(),
  kid_urls: z.array(
    z.object({
      url: ZodTrimmedString,
      language: ZodInstrumentDocumentLanguage,
    }),
  ),
  prospectus_url: ZodTrimmedString.nullable(),
  about_en: ZodTrimmedString.nullable(),
  about_fr: ZodTrimmedString.nullable(),
  about_pl: ZodTrimmedString.nullable(),
  is_adr: z.boolean(),
  shares_ca_wht_rate_at_source: zodDecimalAmount.nullable(),
  is_complex: z.boolean(),
  is_ftt_rate_zero_override: z.boolean(),
  intermediate_instrument_parent_id: ZodIntegerLike.optional().nullable(),
  legal_status: ZodInstrumentMutualFundLegalStatus.nullable(),
  is_etf: z.boolean(),
  pricing_frequency: zodFundinfoPricingFrequency.nullable(),
  last_nav_date: ZodDateString.nullable(),
  last_nav: z.number().nullable(),
  nav_cut_off_buy: ZodTrimmedString.nullable(),
  nav_cut_off_sell: ZodTrimmedString.nullable(),
  admin_cut_off_buy: ZodTrimmedString.nullable(),
  admin_cut_off_sell: ZodTrimmedString.nullable(),
  redemption_period_start_at: ZodDateString.nullable(),
  redemption_period_end_at: ZodDateString.nullable(),
  maturity_target_instrument_id: ZodId.nullable(),
});
export type GetInstrumentOneResponse = z.TypeOf<typeof ZodGetInstrumentOneResponse>;

const ZodInstrumentsPerformanceFilter = z.enum([
  'position_3m',
  'position_6m',
  'position_1y',
  'position_3y',
]);

const ZodGetInstrumentsFilters = z
  .object({
    query: ZodTrimmedString,
    id: createZodArrayable(ZodNumberQuery),
    short_name: createZodArrayable(ZodTrimmedString),
    isin: createZodArrayable(ZodTrimmedString.nullable()),
    performance: ZodInstrumentsPerformanceFilter,
    type: createZodArrayable(ZodInstrumentType.or(ZodB2bInstrumentType)),
    from_redemption_period_start_at: createZodArrayable(ZodTrimmedString),
    to_redemption_period_start_at: createZodArrayable(ZodTrimmedString),
    from_redemption_period_end_at: createZodArrayable(ZodTrimmedString),
    to_redemption_period_end_at: createZodArrayable(ZodTrimmedString),
    is_floor_price_underlying: z.boolean(),
    is_plan_universe_eligible: z.boolean(),
  })
  .partial();

export const getInstrumentsSortColumns: (keyof Omit<GetInstrumentsResponseItem, 'performance'>)[] =
  ['id', 'short_name', 'isin', 'risk'];

const ZodGetInstrumentsQuery = buildZodBoListQueryParsed(
  ZodGetInstrumentsFilters,
  getInstrumentsSortColumns,
  { range: [0, 100] },
);
export type GetInstrumentsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetInstrumentsFilters>,
  (typeof getInstrumentsSortColumns)[number]
>;

export const getInstrumentsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetInstrumentsQuery,
  responseBody: ZodGetInstrumentsResponse,
});

export type GetInstrumentsEndpoint = GetEndpointType<typeof getInstrumentsEndpoint>;

export const getInstrumentsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetInstrumentOneResponse,
});

export type GetInstrumentsIdEndpoint = GetEndpointType<typeof getInstrumentsIdEndpoint>;
